export type MarketingServiceId =
  | 'starter-fine-tuning'
  | 'advanced-sovereign-adaptation'
  | 'frontier-sovereign-model-development'
  | 'edge-browser-ai'
  | 'sovereign-ai-hosting-platform';

export interface MarketingService {
  id: MarketingServiceId;
  name: string;
  label: string;
  price: string;
  priceDetail?: string;
  description: string;
  inclusions: string[];
  bestFor: string;
  ctaLabel: string;
  href: string;
  ctaType: 'purchase' | 'contact';
  featured?: boolean;
}

export interface MarketingAddon {
  name: string;
  price: string;
  description: string;
}

// ---------------- Self-Serve tiers ----------------

export type SelfServeTierId = 'fine-tuning' | 'pre-training' | 'frontier';

export interface SelfServeTier {
  id: SelfServeTierId;
  name: string;
  tagline: string;
  price: string;        // headline, e.g. "$49 CAD"
  amountCents: number;
  currency: 'cad';
  priceUnit: string;    // small caption, e.g. "per training run"
  description: string;
  inclusions: string[];
  idealFor: string[];
  limits: string[];
  ctaLabel: string;
  ctaHref: string;
  featured?: boolean;   // gets the "Most popular" treatment
}

export type TrainingPackageCreditStatus = 'available' | 'active' | 'expired';

export interface TrainingPackageCredit {
  id: string;
  user_id: string;
  purchase_id: string;
  package_id: SelfServeTierId;
  status: TrainingPackageCreditStatus;
  training_job_id: string | null;
  training_request_id: string | null;
  activated_at: string | null;
  expired_at: string | null;
  created_at: string;
  updated_at: string;
}

export const selfServeTiers: SelfServeTier[] = [
  {
    id: 'fine-tuning',
    name: 'Self-Serve Fine-Tuning',
    tagline: 'Adapt open-weight models to your private knowledge.',
    price: '$199 CAD',
    amountCents: 19900,
    currency: 'cad',
    priceUnit: 'starting per training run',

    description:
      'Best for adapting open-weight sovereign models to private company knowledge, workflows, and terminology.',
    inclusions: [
      '24-hour training window',
      'Automated GPU allocation',

      'QLoRA and LoRA support',
      'Upload TXT, MD, CSV, and JSON corpuses',
      'Private workspace storage',
      'Fine-tuning on supported sovereign models',
      'Automatic checkpoint packaging',
      'Downloadable adapters and artifacts',
      'Basic evaluation reporting',
      'Optional hosted inference endpoint',
    ],
    idealFor: [
      'Internal copilots',
      'Documentation assistants',
      'Company terminology adaptation',
      'Departmental AI tools',
      'Lightweight sovereign deployments',
    ],
    limits: [
      'Optimized for small to medium corpuses',
      'Shared compute priority',
      'Community support',
    ],
    ctaLabel: 'Buy Fine-Tuning Package',
    ctaHref: '/purchase?package=fine-tuning&checkout=1',
  },

  {
    id: 'pre-training',
    name: 'Self-Serve Pre-Training',
    tagline: 'Build specialized sovereign intelligence from your data.',
    price: '$399 CAD',
    amountCents: 39900,
    currency: 'cad',
    priceUnit: 'starting per training run',

    description:
      'Build specialized sovereign intelligence from large proprietary datasets and domain-specific corpuses.',
    inclusions: [
      '24-hour dedicated training window',
      'Higher-memory GPU allocation',
      'Continued pre-training workflows',
      'Curriculum and dataset preprocessing',
      'Long-context training support',
      'Tokenizer adaptation support',
      'Intermediate checkpoint exports',
      'Advanced evaluation suite',
      'Quantization-ready artifacts',
      'Hosted model deployment options',
    ],
    idealFor: [
      'Medical AI',
      'Legal AI',
      'Industrial AI',
      'Scientific knowledge systems',
      'Enterprise language specialization',
      'Offline/edge intelligence',
    ],
    limits: [
      'Large dataset uploads supported',
      'Advanced users recommended',
      'Usage-based storage retention',
    ],
    ctaLabel: 'Buy Pre-Training Package',
    ctaHref: '/purchase?package=pre-training&checkout=1',
    featured: true,
  },

  {
    id: 'frontier',
    name: 'Frontier Sovereign Training',
    tagline: 'Frontier-scale experimentation for your AI program.',
    price: '$2,500 CAD',
    amountCents: 250000,
    currency: 'cad',
    priceUnit: 'starting per training run',

    description:
      'Advanced sovereign AI experimentation for organizations building proprietary model capabilities.',
    inclusions: [
      'Premium GPU infrastructure',
      'Large-scale distributed training orchestration',
      'Frontier-scale context windows',
      'Full fine-tuning and adaptation workflows',
      'Synthetic dataset generation tools',
      'Research-grade evaluation tooling',
      'Quantization and deployment pipelines',
      'Priority training queue',
      'Artifact retention and export',
      'Dedicated inference deployment options',
    ],
    idealFor: [
      'Research organizations',
      'AI labs',
      'Defense and aerospace',
      'National sovereign initiatives',
      'Proprietary model development',
      'Experimental architectures',
    ],
    limits: [
      'Requires technical familiarity',
      'Compute-intensive workloads monitored',
      'Subject to fair-use policies',
    ],
    ctaLabel: 'Buy Frontier Package',
    ctaHref: '/purchase?package=frontier&checkout=1',
  },
];

// ---------------- Self-Serve add-ons ----------------

export type SelfServeAddonId =
  | 'extended-training-window'
  | 'extended-optimization'
  | 'dedicated-compute-reservation'
  | 'advanced-evaluation-suite'
  | 'quantized-webgpu-package'
  | 'dedicated-inference-endpoint'
  | 'extended-artifact-retention'
  | 'priority-queue-access';

export interface SelfServeAddon {
  id: SelfServeAddonId;
  name: string;
  price: string;
  amountCents: number;
  currency: 'cad';
  billingType: 'one_time' | 'recurring';
  billingInterval?: 'month';
  unit?: string;          // optional unit description, e.g. "per training run"
  description: string;
  category: 'training' | 'deployment' | 'evaluation';
}

export const selfServeAddons: SelfServeAddon[] = [
  // Training-time upsells
  {
    id: 'extended-training-window',
    name: 'Extended Training Window (+24h)',
    price: '+$149 CAD',
    amountCents: 14900,
    currency: 'cad',
    billingType: 'one_time',
    unit: 'per training run',
    description:
      'Continue training for an additional 24 hours to improve convergence, specialization, and long-context adaptation quality.',
    category: 'training',
  },
  {
    id: 'extended-optimization',
    name: 'Extended Optimization (+72h)',
    price: '+$399 CAD',
    amountCents: 39900,
    currency: 'cad',
    billingType: 'one_time',
    unit: 'per training run',
    description:
      'Three-day extended optimization window. Additional epochs, checkpoint continuations, and evaluation snapshots for serious adaptation work.',
    category: 'training',
  },
  {
    id: 'dedicated-compute-reservation',
    name: 'Dedicated Compute Reservation',
    price: '+$999 CAD',
    amountCents: 99900,
    currency: 'cad',
    billingType: 'one_time',
    unit: 'per training run',
    description:
      'Reserved high-priority compute allocation for your training job. No queue wait, no shared-node contention.',
    category: 'training',
  },
  {
    id: 'advanced-evaluation-suite',
    name: 'Advanced Evaluation Suite',
    price: '+$249 CAD',
    amountCents: 24900,
    currency: 'cad',
    billingType: 'one_time',
    unit: 'per training run',
    description:
      'Red-team analysis, hallucination scoring, and benchmark comparison reports for your trained model.',
    category: 'evaluation',
  },
  // Deployment / hosted services (monthly)
  {
    id: 'quantized-webgpu-package',
    name: 'Quantized WebGPU Package',
    price: '+$99 CAD',
    amountCents: 9900,
    currency: 'cad',
    billingType: 'one_time',
    unit: 'one-time per model',
    description:
      'Browser-ready GGUF / WebGPU packaging optimized for MapleOS and edge deployment.',
    category: 'deployment',
  },
  {
    id: 'dedicated-inference-endpoint',
    name: 'Dedicated Inference Endpoint',
    price: '+$29/month CAD',
    amountCents: 2900,
    currency: 'cad',
    billingType: 'recurring',
    billingInterval: 'month',
    description:
      'Hosted private inference API with scale-to-zero deployment for your trained model.',
    category: 'deployment',
  },
  {
    id: 'extended-artifact-retention',
    name: 'Extended Artifact Retention',
    price: '+$19/month CAD',
    amountCents: 1900,
    currency: 'cad',
    billingType: 'recurring',
    billingInterval: 'month',
    description:
      'Long-term storage for checkpoints, adapters, and intermediate snapshots beyond the default retention window.',
    category: 'deployment',
  },
  {
    id: 'priority-queue-access',
    name: 'Priority Queue Access',
    price: '+$149/month CAD',
    amountCents: 14900,
    currency: 'cad',
    billingType: 'recurring',
    billingInterval: 'month',
    description:
      'Faster training dispatch and reserved compute availability across all your training runs.',
    category: 'deployment',
  },
];

export const enterpriseHero = {
  badge: 'Sovereign AI Infrastructure & SLM Engineering',
  headline: 'Build sovereign AI systems that you control.',
  subheadline:
    'Design, train, deploy, and host AI for governments, enterprises, healthcare, industry, and institutional teams that need privacy and control.',
  primaryCtaLabel: 'Book a Sovereign AI Consultation',
  primaryCtaHref: '/enterprise',
  secondaryCtaLabel: 'View Services',
};

export const capabilityHighlights = [
  {
    title: 'Sovereign ownership',
    description:
      'Retain control over model weights, datasets, inference environments, and deployment decisions without handing strategic assets to third-party platforms.',
    eyebrow: 'Control the asset',
  },
  {
    title: 'Private deployment',
    description:
      'Deploy in your cloud, your facility, a private cluster, or an air-gapped environment with controls built for regulated operations and sensitive workloads.',
    eyebrow: 'Deploy where it belongs',
  },
  {
    title: 'Edge and WebGPU optimization',
    description:
      'Bring low-latency inference to browsers, desktops, and edge environments with WebGPU pipelines and compact model optimization for real-world usage.',
    eyebrow: 'Run closer to the user',
  },
  {
    title: 'Full-stack AI engineering',
    description:
      'Move from dataset strategy to training, evaluation, serving, observability, and integration with one engineering partner accountable for delivery.',
    eyebrow: 'Engineer the full system',
  },
] as const;

export const sovereigntyHighlights = [
  {
    title: 'Data jurisdiction',
    description:
      'When sensitive prompts, documents, and model telemetry flow through foreign infrastructure, jurisdictional ambiguity becomes an operational risk.',
  },
  {
    title: 'Operational control',
    description:
      'Mission-critical AI cannot depend on opaque platform changes, shifting model policies, or external roadmaps that your team does not control.',
  },
  {
    title: 'Compliance exposure',
    description:
      'Healthcare, public sector, research, and industrial teams need traceable deployment choices that support procurement, audit, and governance requirements.',
  },
  {
    title: 'Infrastructure independence',
    description:
      'Sovereign AI infrastructure reduces vendor concentration risk and gives your organization a durable path to internal capability, not rented access.',
  },
] as const;

export const serviceOfferings: MarketingService[] = [
  {
    id: 'starter-fine-tuning',
    name: 'Starter Fine-Tuning',
    label: 'Direct package',
    price: 'Starting at $18,000 CAD',
    description:
      'A fast engagement for teams that need a private model adapted to a defined domain, internal corpus, or operational workflow.',
    inclusions: [
      'Model and use-case scoping workshop',
      'Dataset review and preparation guidance',
      'Supervised fine-tuning configuration',
      'Evaluation set creation and benchmark reporting',
      'Inference packaging for private deployment',
      'Handover documentation and technical review',
    ],
    bestFor:
      'Best for pilot deployments, internal copilots, and domain adaptation projects with a clear dataset and delivery timeline.',
    ctaLabel: 'Start Scoped Purchase',
    href: '/purchase?service=starter-fine-tuning',
    ctaType: 'purchase',
  },
  {
    id: 'advanced-sovereign-adaptation',
    name: 'Advanced Sovereign Adaptation',
    label: 'Enterprise engagement',
    price: 'Starting at $45,000 CAD',
    description:
      'A deeper adaptation program for organizations that need private model behavior tuned to internal processes, governance, and production realities.',
    inclusions: [
      'Multi-stakeholder architecture workshop',
      'Custom evaluation and red-team criteria',
      'Workflow and knowledge integration design',
      'Inference optimization and serving strategy',
      'Governance, observability, and rollout planning',
      'Executive readout and deployment roadmap',
    ],
    bestFor:
      'Best for regulated teams, multi-department rollouts, and organizations moving from proof of concept to production.',
    ctaLabel: 'Talk to CanXP AI',
    href: '/enterprise',
    ctaType: 'contact',
    featured: true,
  },
  {
    id: 'frontier-sovereign-model-development',
    name: 'Frontier Sovereign Model Development',
    label: 'Custom model program',
    price: 'Starting at $250,000 CAD',
    description:
      'A full model development engagement for institutions building proprietary sovereign capability, not just adapting someone else’s system.',
    inclusions: [
      'Training strategy and compute planning',
      'Tokenizer, architecture, and dataset design',
      'Synthetic data and curriculum development',
      'Safety evaluation and alignment testing',
      'Serving stack and deployment engineering',
      'Leadership reporting and commercialization planning',
    ],
    bestFor:
      'Best for national, industrial, and institutional programs that require proprietary models, internal IP creation, and long-term control.',
    ctaLabel: 'Request Architecture Review',
    href: '/enterprise',
    ctaType: 'contact',
  },
  {
    id: 'edge-browser-ai',
    name: 'Edge & Browser AI Optimization',
    label: 'Direct package',
    price: 'Starting at $22,000 CAD',
    description:
      'Optimize compact models for browser, desktop, kiosk, and edge environments where latency, privacy, and offline capability matter.',
    inclusions: [
      'Model compression and runtime tuning',
      'WebGPU deployment pipeline design',
      'Browser and edge benchmark profiling',
      'Latency, memory, and throughput optimization',
      'Client-side inference integration support',
      'Deployment runbook and technical handoff',
    ],
    bestFor:
      'Best for browser products, field devices, secure workstations, and on-prem user experiences that cannot rely on constant cloud inference.',
    ctaLabel: 'Start Scoped Purchase',
    href: '/purchase?service=edge-browser-ai',
    ctaType: 'purchase',
  },
  {
    id: 'sovereign-ai-hosting-platform',
    name: 'Sovereign AI Hosting Platform',
    label: 'Managed infrastructure',
    price: 'Starting at $8,000/month CAD',
    priceDetail: 'Enterprise deployments: $25,000–$250,000+/month',
    description:
      'Private hosting, serving, and operational support for organizations that need secure sovereign inference infrastructure without compromise.',
    inclusions: [
      'Private hosting architecture and rollout planning',
      'Model serving, scaling, and observability stack',
      'Deployment controls for compliance-sensitive teams',
      'Dedicated environment management and uptime support',
      'Private networking and access control configuration',
      'Ongoing optimization, reporting, and engineering support',
    ],
    bestFor:
      'Best for government, healthcare, university, and enterprise deployments that require long-term operational ownership and managed reliability.',
    ctaLabel: 'Discuss Your Deployment',
    href: '/enterprise',
    ctaType: 'contact',
    featured: true,
  },
];

export function getSelfServeTierById(tierId: string | null | undefined) {
  return selfServeTiers.find((tier) => tier.id === tierId) || null;
}

export function getSelfServeAddonById(addonId: string | null | undefined) {
  return selfServeAddons.find((addon) => addon.id === addonId) || null;
}

export const addonServices: MarketingAddon[] = [

  {
    name: 'Dataset Engineering',
    price: '$15K+',
    description: 'Corpus design, cleanup, labeling strategy, and domain-specific training set preparation.',
  },
  {
    name: 'Synthetic Data Generation',
    price: '$25K+',
    description: 'Synthetic expansion pipelines for scarce, sensitive, or specialized training data.',
  },
  {
    name: 'AI Safety & Alignment Testing',
    price: '$20K+',
    description: 'Safety evaluation, adversarial testing, and policy alignment for high-stakes deployments.',
  },
  {
    name: 'RAG / Knowledge Integration',
    price: '$30K+',
    description: 'Private retrieval, indexing, and knowledge orchestration across internal content systems.',
  },
  {
    name: 'WebGPU Deployment Pipeline',
    price: '$20K+',
    description: 'Runtime packaging and deployment tooling for performant browser-based inference.',
  },
  {
    name: 'Private GPU Cluster Deployment',
    price: '$100K+',
    description: 'Cluster architecture, orchestration, security controls, and model serving setup.',
  },
  {
    name: 'Dedicated H200 Infrastructure',
    price: 'Custom',
    description: 'Reserved high-performance infrastructure for advanced training and sovereign hosting programs.',
  },
  {
    name: 'Air-Gapped Deployment',
    price: 'Custom',
    description: 'Deployment design for isolated environments with strict operational and security boundaries.',
  },
  {
    name: 'AI Surface Integration',
    price: 'Custom',
    description: 'Embed AI into portals, internal tools, operational systems, and customer-facing workflows.',
  },
  {
    name: 'MapleOS AI Deployment',
    price: 'Custom',
    description: 'Deploy models and workflows inside CanXP AI operating environments and controlled user surfaces.',
  },
];

export const supportedSectors = [
  'Governments',
  'Healthcare',
  'Universities',
  'Industry',
  'Enterprises',
] as const;

export const directPurchaseServiceIds: MarketingServiceId[] = ['starter-fine-tuning', 'edge-browser-ai'];

export const getMarketingServiceById = (serviceId?: string | null) =>
  serviceOfferings.find((service) => service.id === serviceId);
